import { useMemo } from "react";
import type { Turn } from "../chatState.ts";
import { renderMarkdown } from "../markdown.ts";
import { serverLabel, toolLabel } from "../toolLabels.ts";
import { ApprovalCard } from "./ApprovalCard.tsx";

export function Message({
  turn,
  busy,
  onDecide,
  onResume,
}: {
  turn: Turn;
  busy: boolean;
  onDecide: (id: string, approve: boolean) => void;
  onResume: () => void;
}) {
  const html = useMemo(() => (turn.role === "assistant" ? renderMarkdown(turn.text) : ""), [turn.role, turn.text]);

  if (turn.role === "user") {
    return (
      <div className="msg msg-user">
        <p>{turn.text}</p>
      </div>
    );
  }

  const thinking = !turn.done && !turn.text && turn.tools.every((t) => t.status !== "running");
  return (
    <div className="msg msg-agent" aria-live="polite">
      {turn.tools.length > 0 && (
        <ul className="tools" aria-label="Verktyg som används">
          {turn.tools.map((t) => (
            <li key={t.id} className={`tool tool-${t.status}`}>
              <span className="tool-dot" aria-hidden="true" />
              <span className="tool-server">{serverLabel(t.server, t.name)}</span>
              <span>{toolLabel(t.name)}</span>
            </li>
          ))}
        </ul>
      )}
      {thinking && (
        <p className="thinking">
          <span className="dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          Tänker…
        </p>
      )}
      {turn.text && <div className="prose" dangerouslySetInnerHTML={{ __html: html }} />}
      {turn.consent && (
        <section className="card consent">
          <h3>Logga in i {serverLabel(turn.consent.server)}</h3>
          <p>Första gången behöver du ge agenten åtkomst. Den använder bara dina egna behörigheter.</p>
          <div className="approval-actions">
            <a className="btn btn-primary" href={turn.consent.link} target="_blank" rel="noopener noreferrer">
              Logga in →
            </a>
            <button className="btn btn-secondary" disabled={busy} onClick={onResume}>
              Jag är klar – fortsätt
            </button>
          </div>
        </section>
      )}
      {turn.approvals.map((a) => (
        <ApprovalCard key={a.id} request={a} disabled={busy} onDecide={(approve) => onDecide(a.id, approve)} />
      ))}
      {turn.error && (
        <p className="error" role="alert">
          {turn.error}
        </p>
      )}
    </div>
  );
}
